import React, { useCallback, useEffect, useRef, useState } from "react";
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogActions,
	IconButton,
	Typography,
	Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckIcon from "@mui/icons-material/Check";

const ImageUploadHandler = ({
	onCapture,
	accept = "image/*",
	disabled = false,
	facingMode = "environment",
}) => {
	const fileInputRef = useRef(null);
	const videoRef = useRef(null);
	const streamRef = useRef(null);
	const canvasRef = useRef(null);

	const [cameraOpen, setCameraOpen] = useState(false);
	const [cameraError, setCameraError] = useState(null);
	const [capturedPreviewUrl, setCapturedPreviewUrl] = useState(null);
	const [capturedBlob, setCapturedBlob] = useState(null);

	const stopStream = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
		}
	}, []);

	const closeCamera = useCallback(() => {
		stopStream();
		setCameraOpen(false);
		setCameraError(null);
		if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
		setCapturedPreviewUrl(null);
		setCapturedBlob(null);
	}, [stopStream, capturedPreviewUrl]);

	// Stop the stream if the component unmounts while camera is open.
	useEffect(() => {
		return () => {
			stopStream();
			if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const startCameraStream = useCallback(async () => {
		setCameraError(null);
		setCameraOpen(true);

		if (!navigator.mediaDevices?.getUserMedia) {
			setCameraError(
				"Camera capture isn't supported in this browser. Use Attach File instead.",
			);
			return;
		}

		try {
			// Triggers the native browser/OS camera PERMISSION prompt.
			const stream = await navigator.mediaDevices.getUserMedia({
				video: { facingMode },
				audio: false,
			});
			streamRef.current = stream;
			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play();
			}
		} catch (err) {
			if (err?.name === "NotAllowedError") {
				setCameraError(
					"Camera access was denied. Allow camera permission in your browser/device settings, or use Attach File.",
				);
			} else if (err?.name === "NotFoundError") {
				setCameraError(
					"No camera was found on this device. Use Attach File instead.",
				);
			} else {
				setCameraError(
					"Couldn't access the camera. Use Attach File instead.",
				);
			}
		}
	}, [facingMode]);

	const handleSnapPhoto = useCallback(() => {
		const video = videoRef.current;
		if (!video || !video.videoWidth) return;

		const canvas = canvasRef.current;
		canvas.width = video.videoWidth;
		canvas.height = video.videoHeight;
		const ctx = canvas.getContext("2d");
		ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

		// Freeze the live feed while the user reviews the still frame.
		stopStream();

		canvas.toBlob(
			(blob) => {
				if (!blob) return;
				setCapturedBlob(blob);
				setCapturedPreviewUrl(URL.createObjectURL(blob));
			},
			"image/jpeg",
			0.92,
		);
	}, [stopStream]);

	const handleRetake = useCallback(() => {
		if (capturedPreviewUrl) URL.revokeObjectURL(capturedPreviewUrl);
		setCapturedPreviewUrl(null);
		setCapturedBlob(null);
		startCameraStream();
	}, [capturedPreviewUrl, startCameraStream]);

	const handleConfirmPhoto = useCallback(() => {
		if (!capturedBlob) return;
		const file = new File([capturedBlob], `photo-${Date.now()}.jpg`, {
			type: "image/jpeg",
		});
		onCapture?.(file);
		closeCamera();
	}, [capturedBlob, onCapture, closeCamera]);

	const handleFileChange = useCallback(
		(event) => {
			const file = event.target.files?.[0];
			if (file) onCapture?.(file);
			// reset so selecting the same file again still fires onChange
			event.target.value = "";
		},
		[onCapture],
	);

	return (
		<>
			<Box sx={{ display: "flex", gap: 1 }}>
				<Button
					variant="outlined"
					size="small"
					disabled={disabled}
					onClick={startCameraStream}
					startIcon={<CameraAltIcon fontSize="small" />}
				>
					Camera
				</Button>
				<Button
					variant="outlined"
					size="small"
					disabled={disabled}
					onClick={() => fileInputRef.current?.click()}
				>
					📎 Attach File
				</Button>
			</Box>

			<input
				ref={fileInputRef}
				type="file"
				accept={accept}
				hidden
				onChange={handleFileChange}
			/>

			<Dialog
				open={cameraOpen}
				onClose={closeCamera}
				maxWidth="sm"
				fullWidth
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						px: 2,
						pt: 1.5,
					}}
				>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
						{capturedPreviewUrl ? "Review Photo" : "Take Photo"}
					</Typography>
					<IconButton size="small" onClick={closeCamera}>
						<CloseIcon fontSize="small" />
					</IconButton>
				</Box>

				<DialogContent>
					{cameraError ? (
						<Alert severity="error">{cameraError}</Alert>
					) : (
						<Box
							sx={{
								position: "relative",
								width: "100%",
								borderRadius: 1,
								overflow: "hidden",
								bgcolor: "black",
								aspectRatio: "4 / 3",
							}}
						>
							<video
								ref={videoRef}
								playsInline
								muted
								autoPlay
								style={{
									width: "100%",
									height: "100%",
									objectFit: "cover",
									display: capturedPreviewUrl
										? "none"
										: "block",
								}}
							/>
							{capturedPreviewUrl && (
								<img
									src={capturedPreviewUrl}
									alt="Captured preview"
									style={{
										width: "100%",
										height: "100%",
										objectFit: "cover",
										display: "block",
									}}
								/>
							)}
						</Box>
					)}
					<canvas ref={canvasRef} style={{ display: "none" }} />
				</DialogContent>

				<DialogActions sx={{ px: 2, pb: 2 }}>
					{cameraError ? (
						<Button onClick={closeCamera}>Close</Button>
					) : capturedPreviewUrl ? (
						<>
							<Button
								onClick={handleRetake}
								startIcon={<ReplayIcon fontSize="small" />}
							>
								Retake
							</Button>
							<Button
								variant="contained"
								onClick={handleConfirmPhoto}
								startIcon={<CheckIcon fontSize="small" />}
							>
								Use Photo
							</Button>
						</>
					) : (
						<Button
							variant="contained"
							onClick={handleSnapPhoto}
							startIcon={<CameraAltIcon fontSize="small" />}
						>
							Capture
						</Button>
					)}
				</DialogActions>
			</Dialog>
		</>
	);
};

export default ImageUploadHandler;
